import { useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegister, type OnboardingAnswers } from '@/hooks/use-auth';
import { isAuthenticated } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/register')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
  component: RegisterPage,
});

type Option = { value: string; label: string };

const ROLES: Option[] = [
  { value: 'owner', label: 'Dueño / CEO' },
  { value: 'network_engineer', label: 'Ingeniero de red' },
  { value: 'noc', label: 'NOC / Operaciones' },
  { value: 'other', label: 'Otro' },
];
const SUBSCRIBERS: Option[] = [
  { value: '<500', label: 'Menos de 500' },
  { value: '500-2k', label: '500 – 2.000' },
  { value: '2k-10k', label: '2.000 – 10.000' },
  { value: '10k-50k', label: '10.000 – 50.000' },
  { value: '>50k', label: 'Más de 50.000' },
];
const RIRS: Option[] = [
  { value: 'LACNIC', label: 'LACNIC (Latinoamérica)' },
  { value: 'ARIN', label: 'ARIN (Norteamérica)' },
  { value: 'RIPE', label: 'RIPE (Europa)' },
  { value: 'APNIC', label: 'APNIC (Asia-Pacífico)' },
  { value: 'AFRINIC', label: 'AFRINIC (África)' },
];
const CGNAT: Option[] = [
  { value: 'deterministic', label: 'Sí, determinístico' },
  { value: 'dynamic', label: 'Sí, dinámico' },
  { value: 'no', label: 'No usamos CGNAT' },
  { value: 'planning', label: 'Lo estamos planeando' },
];
const TOOLS: Option[] = [
  { value: 'spreadsheets', label: 'Hojas de cálculo' },
  { value: 'phpipam', label: 'phpIPAM' },
  { value: 'netbox', label: 'NetBox' },
  { value: 'other_tool', label: 'Otra herramienta' },
  { value: 'none', label: 'Nada formal' },
];
const VENDORS: Option[] = [
  { value: 'mikrotik', label: 'MikroTik' },
  { value: 'juniper', label: 'Juniper' },
  { value: 'cisco', label: 'Cisco' },
  { value: 'arista', label: 'Arista' },
  { value: 'huawei', label: 'Huawei' },
  { value: 'other', label: 'Otro' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();
  const [step, setStep] = useState<1 | 2>(1);

  // Account
  const [company, setCompany] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Questionnaire
  const [answers, setAnswers] = useState<OnboardingAnswers>({ vendors: [] });

  const accountValid =
    company.trim().length > 0 &&
    EMAIL_RE.test(email) &&
    password.length >= 8 &&
    password === confirm;

  function set<K extends keyof OnboardingAnswers>(key: K, value: OnboardingAnswers[K]) {
    setAnswers((a) => ({ ...a, [key]: value }));
  }

  function toggleVendor(v: string) {
    setAnswers((a) => {
      const current = a.vendors ?? [];
      return {
        ...a,
        vendors: current.includes(v) ? current.filter((x) => x !== v) : [...current, v],
      };
    });
  }

  function submit() {
    register.mutate(
      {
        email: email.trim(),
        password,
        display_name: displayName.trim() || email.trim(),
        company: company.trim(),
        onboarding: answers,
      },
      { onSuccess: () => void navigate({ to: '/' }) },
    );
  }

  const errorMessage = register.error instanceof Error ? register.error.message : null;

  return (
    <div className="grid min-h-screen place-items-center bg-blaze-base px-4 py-8 text-foreground">
      <div className="w-full max-w-md">
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <img src="/blaze-mark.svg" alt="BlazeIPAM" className="size-10" />
          <h1 className="text-xl font-bold tracking-tight">
            Crea tu workspace en Blaze
            <span className="mx-[1px] inline-block size-2 rounded-full bg-primary align-middle shadow-[0_0_10px_var(--accent-cyan)]" />
            IPAM
          </h1>
          <p className="text-xs text-muted-foreground">
            Cada ISP obtiene su propio espacio aislado. Gratis para empezar.
          </p>
        </div>

        {/* Stepper */}
        <div className="mb-4 flex items-center justify-center gap-2 text-[11px]">
          <StepDot n={1} active={step === 1} done={step > 1} label="Cuenta" />
          <span className="h-px w-8 bg-border" />
          <StepDot n={2} active={step === 2} done={false} label="Cuestionario" />
        </div>

        <div className="rounded-lg border border-border bg-blaze-elev1 p-5 shadow-xl">
          {step === 1 ? (
            <div className="space-y-3">
              <Field label="Empresa / ISP" htmlFor="company">
                <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="FibraMax ISP" />
              </Field>
              <Field label="Tu nombre" htmlFor="name">
                <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Juan Pérez" />
              </Field>
              <Field label="Correo" htmlFor="email">
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="noc@tu-isp.do" autoComplete="email" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contraseña" htmlFor="password">
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="mín. 8 caracteres" autoComplete="new-password" />
                </Field>
                <Field label="Confirmar" htmlFor="confirm">
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
                </Field>
              </div>
              {confirm.length > 0 && password !== confirm && (
                <p className="text-xs text-destructive">Las contraseñas no coinciden.</p>
              )}
              <Button type="button" className="w-full gap-2" disabled={!accountValid} onClick={() => setStep(2)}>
                Continuar <ArrowRight className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <SelectField label="¿Cuál es tu rol?" value={answers.role} onChange={(v) => set('role', v)} options={ROLES} />
              <SelectField label="¿Cuántos suscriptores tienes?" value={answers.subscribers} onChange={(v) => set('subscribers', v)} options={SUBSCRIBERS} />
              <SelectField label="¿Qué RIR te asigna direcciones?" value={answers.rir} onChange={(v) => set('rir', v)} options={RIRS} />
              <SelectField label="¿Usas CGNAT?" value={answers.cgnat} onChange={(v) => set('cgnat', v)} options={CGNAT} />
              <SelectField label="¿Cómo gestionas IPs hoy?" value={answers.current_tool} onChange={(v) => set('current_tool', v)} options={TOOLS} />

              <div className="space-y-1.5">
                <Label className="text-xs">¿Qué marcas de equipos usas?</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {VENDORS.map((v) => (
                    <label key={v.value} className="flex cursor-pointer items-center gap-2 rounded border border-border px-2 py-1.5 text-xs hover:bg-accent">
                      <Checkbox
                        checked={(answers.vendors ?? []).includes(v.value)}
                        onCheckedChange={() => toggleVendor(v.value)}
                      />
                      {v.label}
                    </label>
                  ))}
                </div>
              </div>

              <Field label="ASN (opcional)" htmlFor="asn">
                <Input id="asn" value={answers.asn ?? ''} onChange={(e) => set('asn', e.target.value)} placeholder="AS264xxx" className="font-mono" />
              </Field>

              {errorMessage && (
                <p className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertCircle className="size-3.5" />
                  {errorMessage}
                </p>
              )}

              <div className="flex gap-2">
                <Button type="button" variant="outline" className="gap-1.5" onClick={() => setStep(1)} disabled={register.isPending}>
                  <ArrowLeft className="size-4" /> Atrás
                </Button>
                <Button type="button" className="flex-1 gap-2" onClick={submit} disabled={register.isPending}>
                  {register.isPending ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
                  Crear workspace
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
}

function StepDot({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={cn(
          'grid size-5 place-items-center rounded-full text-[10px] font-semibold',
          active || done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        {n}
      </span>
      <span className={cn(active ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
    </span>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  options: Option[];
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Selecciona…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
